import React, { FC, useContext, useEffect, useMemo, useState } from 'react';

import {
  Modal,
  Button,
  H2,
  TextArea,
  H3,
  Tooltip,
  InfoIcon,
  Checkbox,
  IDropdownItem,
  BottomIcon,
  Dropdown,
  Paragraph,
} from '@smartshell/ui-kit';
import cn from 'classnames';

import {
  HorizontalLeftTextIcon,
  VerticalCenterTextIcon,
  VerticalLeftTextIcon,
  VerticalRightTextIcon,
  HorizontalCenterTextIcon,
  HorizontalRightTextIcon,
  ColorPicker,
} from 'components';
import { LocalizationContext } from 'localization';

import { getBlockHeight, getBlockWidth } from '../../utils';
import { IAlignItems, ITextEditModalProps } from './interfaces';
import s from './TextEditModal.module.scss';

export const TextEditModal: FC<ITextEditModalProps> = ({
  isOpen,
  initialText,
  initialTextStyle,
  onSave,
  onCancel,
  setDefaultTextStyle,
}) => {
  const { t } = useContext(LocalizationContext);
  const [text, setText] = useState('');

  const [textStyle, setTextStyle] = useState({ ...initialTextStyle });
  const [isSaveTextStyle, setIsSaveTextStyle] = useState(false);
  const [isSizeDropdownOpen, setIsSizeDropdownOpen] = useState(false);

  useEffect(() => {
    setTextStyle(initialTextStyle);
  }, [initialTextStyle]);

  useEffect(() => {
    setText(initialText ?? '');
  }, [initialText]);

  const alignItems: IAlignItems = {
    verticalAlign: [
      { value: 'flex-start', icon: <VerticalLeftTextIcon /> },
      { value: 'center', icon: <VerticalCenterTextIcon /> },
      { value: 'flex-end', icon: <VerticalRightTextIcon /> },
    ],
    horizontalAlign: [
      { value: 'flex-start', icon: <HorizontalLeftTextIcon /> },
      { value: 'center', icon: <HorizontalCenterTextIcon /> },
      { value: 'flex-end', icon: <HorizontalRightTextIcon /> },
    ],
  };

  const fontSizeVar: Array<IDropdownItem<number>> = [
    { value: 8, title: '8' },
    { value: 10, title: '10' },
    { value: 12, title: '12' },
    { value: 14, title: '14' },
    { value: 16, title: '16' },
    { value: 18, title: '18' },
    { value: 20, title: '20' },
    { value: 22, title: '22' },
  ];

  const { width, height } = useMemo(() => {
    const width = getBlockWidth(text, textStyle.fontSize);
    const height = getBlockHeight(text, textStyle.fontSize);

    return { width, height };
  }, [text, textStyle.fontSize]);

  if (!isOpen) return null;

  return (
    <Modal className={s.container} onClose={onCancel}>
      <div className={s.content}>
        <H2>{t('host.map.text.modal.title')}</H2>
        <TextArea
          value={text}
          onChange={(value) => setText(value)}
          title={t('host.map.text.placeholder')}
          placeholder={t('host.map.text.placeholder')}
          name={'text'}
          variant="secondary"
          limit={256}
          className={s.textarea}
          errorWrapperClassName={s.textarea_container}
          bordered
        />
        <div className={s.preview_title}>
          <H3>{t('host.map.text.modal.preview')}</H3>
          <Tooltip
            content={t('host.map.text.modal.preview.tooltip')}
            contentClassName={s.tooltip}
          >
            <InfoIcon color="link-info" size="s" />
          </Tooltip>
        </div>
        <div className={s.preview}>
          <div className={s.preview__block}>
            <div
              className={s.textContentSingleLine}
              style={{
                fontSize: `${textStyle.fontSize}px`,
                justifyContent: textStyle.horizontalAlign,
                color: textStyle.color,
                alignItems: textStyle.verticalAlign,
                textAlign:
                  textStyle.verticalAlign === 'flex-start'
                    ? 'left'
                    : textStyle.verticalAlign === 'flex-end'
                    ? 'right'
                    : 'center',
                width: `${width}px`,
                height: `${height}px`,
              }}
            >
              {text}
            </div>
          </div>
          <div className={s.align}>
            <div className={s.align__block}>
              {alignItems.horizontalAlign.map(({ value, icon }) => (
                <button
                  className={cn(
                    s.align__button,
                    value === textStyle.horizontalAlign &&
                      s.align__button_current
                  )}
                  key={value}
                  onClick={() =>
                    setTextStyle((prev) => ({
                      ...prev,
                      horizontalAlign: value,
                    }))
                  }
                >
                  {icon}
                </button>
              ))}
            </div>
            <div className={s.align__block}>
              {alignItems.verticalAlign.map(({ value, icon }) => (
                <button
                  className={cn(
                    s.align__button,
                    value === textStyle.verticalAlign && s.align__button_current
                  )}
                  key={value}
                  onClick={() =>
                    setTextStyle((prev) => ({ ...prev, verticalAlign: value }))
                  }
                >
                  {icon}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className={s.styles}>
          <div className={s.size}>
            <div className={s.size__block}>
              <div className={s.size__value}>
                <Paragraph>{textStyle.fontSize}</Paragraph>
              </div>
              <button
                className={cn(
                  s.size__button,
                  isSizeDropdownOpen && s.size__button_active
                )}
                onClick={() => setIsSizeDropdownOpen(!isSizeDropdownOpen)}
              >
                <BottomIcon color="primary" />
              </button>
            </div>
            {isSizeDropdownOpen && (
              <Dropdown
                items={fontSizeVar}
                onChange={(value) => {
                  setTextStyle((prev) => ({ ...prev, fontSize: value }));
                  setIsSizeDropdownOpen(false);
                }}
                className={s.size__dropdown}
                renderItem={({ value }) => (
                  <div
                    className={cn(
                      s.size__dropdown__item,
                      value === textStyle.fontSize &&
                        s.size__dropdown__item_current
                    )}
                  >
                    {value}
                  </div>
                )}
              />
            )}
          </div>
          <ColorPicker
            color={textStyle.color}
            setColor={(color) => setTextStyle((prev) => ({ ...prev, color }))}
            variant="secondary"
            colors={['#707072', '#2C87FD', '#6963FF', '#FF5757', '#57FF90']}
          />
        </div>
        <Checkbox
          value={isSaveTextStyle}
          onChange={() => setIsSaveTextStyle(!isSaveTextStyle)}
          label={t('host.map.text.modal.saveStyle')}
          className={s.checkbox}
        />
        <div className={s.actions}>
          <Button
            fullWidth
            onClick={() => {
              const trimmed = text.trim();

              if (trimmed || initialText) {
                onSave(trimmed, textStyle);

                if (isSaveTextStyle) {
                  setDefaultTextStyle({ ...textStyle });
                  setIsSaveTextStyle(false);
                }
              } else {
                onCancel();
              }
            }}
            variant="primary"
          >
            {t('save')}
          </Button>
          <Button
            className={s.button}
            fullWidth
            onClick={() => {
              setText('');
            }}
            disabled={text.length === 0}
            variant="outline"
          >
            {t('host.map.text.modal.button')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
